import {throttle} from '../utils/throttle';

const selectors = {
  section: '[data-section="header"]',
  parent: '.header__menu-parent',
  headerMenu: '.header__menu',
  navbar: '[data-desktop-menu]',
  topbar: '[data-header-topbar]',
  searchContainer: '[data-search-container]',
  searchInput: '[data-search-input]',
  searchClose: '[data-search-close]',
  menuLevel1: '[data-menu-level-1]',
  wishlistCount: '[data-header-wishlist-count]',
};

const classes = {
  navbarPinned: 'navbar--pinned',
  navbarUnpinned: 'navbar--unpinned',
  bodyNavbarPinned: 'navbar-is-pinned',
  searchActive: 'header__search-container--active',
  bodySearchActive: 'search-is-active',
  searchCloseActive: 'search-bar__close--active',
};

class Header {
  constructor(container) {
    this.container = container;
    this.parentLinks = container.querySelectorAll(selectors.parent);
    this.searchContainer = container.querySelector(selectors.searchContainer);
    this.searchInput = container.querySelector(selectors.searchInput);
    this.searchClose = container.querySelector(selectors.searchClose);
    this.navbar = container.querySelector(selectors.navbar);
    this.topbar = container.querySelector(selectors.topbar);
    this.wishlistCountElem = container.querySelector(selectors.wishlistCount);

    this.timeoutID = undefined;
    this.activeMenu = false;

    this.enableHeaderSticky = this.container.dataset?.headerSticky === 'true';
  }

  init() {
    this.parentLinks.forEach((parentLink) => {
      parentLink.addEventListener('mouseenter', () => {
        this.timeoutID = setTimeout(this.showMenu.bind(this), 250, parentLink);
      });

      parentLink.addEventListener('mouseleave', () => {
        this.hideMenu();
      });
    });

    if (this.enableHeaderSticky) {
      this.setHeaderHeightVar();
      window.addEventListener('resize', this.setHeaderHeightVar.bind(this));
    }

    this.handleNavVisibilityOnScroll();

    this.initSearchInputStateHandler();

    this.initSearchSuggestions();

    this.initSearchCloseStateHandler();

    this.initMainMenu();

    this.initMobileMenu();

    this.initWishlistCount();

    // Handle close button click
    this.searchClose?.addEventListener('click', () => {
      this.searchInput.value = '';
      this.searchInput.focus();
      this.searchClose.classList.remove(classes.searchCloseActive);
    });
  }

  showMenu(parent) {
    this.clearActiveMenu();

    // Update active menu
    parent.classList.add('is-hovered');
    this.activeMenu = parent;
  }

  hideMenu() {
    clearTimeout(this.timeoutID);
    this.clearActiveMenu();
  }

  clearActiveMenu() {
    if (!this.activeMenu) return;
    this.activeMenu.classList.remove('is-hovered');
  }

  setHeaderHeightVar() {
    this.navbar = this.container.querySelector(selectors.navbar);
    this.topbar = this.container.querySelector(selectors.topbar);

    if (this.topbar) {
      const topbarHeight = this.topbar.offsetHeight - 50;
      document.documentElement.style.setProperty('--header-topbar-height', `${topbarHeight}px`);
    }

    if (this.navbar) {
      const navbarHeight = this.navbar.offsetHeight;
      document.documentElement.style.setProperty('--desktop-menu-height', `${navbarHeight}px`);
    }
  }

  handleNavVisibilityOnScroll() {
    if (!this.enableHeaderSticky || !this.navbar) return;

    const headerNavbar = this.navbar;
    const headerDiv = document.querySelector('#shopify-section-header');
    const TOP_THRESHOLD = 300;

    let lastScroll = 0;
    let ticking = false;

    const updateNavbar = () => {
      const currentScroll = window.scrollY || window.pageYOffSet;

      // Always show navbar when near the top of the page
      if (currentScroll < TOP_THRESHOLD) {
        headerNavbar.classList.add(classes.navbarPinned);
        headerDiv.classList.remove(classes.navbarUnpinned);
        headerNavbar.classList.remove(classes.navbarUnpinned);
        document.body.classList.remove(classes.bodyNavbarPinned);
        ticking = false;
        return;
      }

      if (currentScroll > lastScroll) {
        // Scrolling down
        headerNavbar.classList.add(classes.navbarUnpinned);
        headerDiv.classList.add(classes.navbarUnpinned);
        headerNavbar.classList.remove(classes.navbarPinned);
        document.body.classList.add(classes.bodyNavbarPinned);
        // Close all open header__menu-dropdown elements
        const openDropdowns = document.querySelectorAll('.header__menu-dropdown.open');
        openDropdowns.forEach((dropdown) => {
          dropdown.classList.remove('open');
        });
      } else {
        // Scrolling up
        headerNavbar.classList.add(classes.navbarPinned);
        headerDiv.classList.remove(classes.navbarUnpinned);
        headerNavbar.classList.remove(classes.navbarUnpinned);
        document.body.classList.remove(classes.bodyNavbarPinned);
      }

      lastScroll = currentScroll;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateNavbar);
        ticking = true;
      }
    };

    // Initialize scroll event listener
    window.addEventListener('scroll', onScroll, {passive: true});
  }

  initSearchInputStateHandler() {
    if (!this.searchInput) return;

    // Expand input on focus
    this.searchInput.addEventListener('focus', () => {
      this.searchContainer.classList.add(classes.searchActive);
      document.body.classList.add(classes.bodySearchActive);
      // Close all open header__menu-dropdown elements
      const openDropdowns = document.querySelectorAll('.header__menu-dropdown.open');
      openDropdowns.forEach((dropdown) => {
        dropdown.classList.remove('open');
      });
    });

    this.searchInput.addEventListener('input', () => {
      if (
        this.searchContainer.classList.contains(classes.searchActive) ||
        document.body.classList.contains(classes.bodySearchActive)
      )
        return;
      this.searchContainer.classList.add(classes.searchActive);
      document.body.classList.add(classes.bodySearchActive);
    });

    // Collapse input on click outside
    document.addEventListener('click', (event) => {
      const isSearchContainer = this.searchContainer && this.searchContainer.contains(event.target);
      const isSearchWidget = this.searchWidget && this.searchWidget.contains(event.target);

      if (isSearchContainer || isSearchWidget) return;

      this.searchContainer.classList.remove(classes.searchActive);
      document.body.classList.remove(classes.bodySearchActive);
      this.searchInput.value = '';
      this.searchClose.classList.remove(classes.searchCloseActive);
      // Close all open header__menu-dropdown elements
      const openDropdowns = document.querySelectorAll('.header__menu-dropdown.open');
      openDropdowns.forEach((dropdown) => {
        dropdown.classList.remove('open');
      });

      document.dispatchEvent(new CustomEvent('tagalys:hide-search-suggestions'));
    });
  }

  initSearchCloseStateHandler() {
    // Handle close button visibility
    if (!this.searchInput || !this.searchClose) return;

    this.searchInput.addEventListener('keyup', () => {
      if (this.searchInput.value === '') {
        this.searchClose.classList.remove(classes.searchCloseActive);
        return;
      }

      this.searchClose.classList.add(classes.searchCloseActive);
    });
  }

  initMobileMenu() {
    // Helper function to toggle classes
    const toggleClasses = (el, ...classes) => {
      if (el) {
        classes.forEach((cls) => el.classList.toggle(cls));
      }
    };

    // Helper function to close accordions
    const closeAccordions = (selector, ...classesToRemove) => {
      document.querySelectorAll(selector).forEach((el) => {
        if (el.classList.contains('active')) {
          const scrollHeight = el.scrollHeight;
          el.style.maxHeight = `${scrollHeight}px`;
          toggleClasses(el, 'active');
          toggleClasses(el.previousElementSibling, ...classesToRemove);
        }
      });
    };

    document.addEventListener('DOMContentLoaded', () => {
      const toggleAccordion = (event, level) => {
        const currentTarget = event.currentTarget;
        const content = currentTarget.nextElementSibling;

        if (level === 3) {
          document.querySelectorAll('.level3.active').forEach((el) => {
            if (el !== content) {
              el.classList.toggle('active');
              el.style.maxHeight = null;
              el.previousElementSibling.classList.toggle('active');
            }
          });
          currentTarget.classList.toggle('active');
          const scrollHeight = content.scrollHeight;
          if (content.classList.contains('active')) {
            content.style.maxHeight = null;
          } else {
            content.style.maxHeight = `${scrollHeight}px`;
          }
          content.classList.toggle('active');
        }

        if (level === 2) {
          content.classList.toggle('active');
          if (content.classList.contains('active')) {
            currentTarget.closest('.level1').style.left = '-100%';
          } else {
            currentTarget.closest('.level1').style.left = '0';
          }
        }
      };

      // active mobile nav
      document.querySelector('.header__top-hamburger')?.addEventListener('click', () => {
        const mobileNav = document.querySelector('.mobile-menu');
        if (mobileNav) {
          mobileNav.style.left = '0';
        }
        document.querySelector('.mobile-menu__overlay')?.classList.add('active');
      });

      // Close mobile nav
      document.querySelectorAll('.mobile-menu__close')?.forEach((menu) => {
        menu.addEventListener('click', () => {
          const mobileNav = document.querySelector('.mobile-menu');
          if (mobileNav) {
            mobileNav.style.left = '-100%';
          }
          const level1Nav = mobileNav?.querySelector(selectors.menuLevel1);
          if (level1Nav) {
            level1Nav.style.left = '0';
          }
          closeAccordions('.level2', 'active');
          closeAccordions('.level3', 'active');
          document.querySelector('.mobile-menu__overlay')?.classList.remove('active');
        });
      });

      const moveLevel1AndCloseAccordions = () => {
        const level1 = document.querySelector('.level1.accordion');
        level1.style.left = '0';
        // Delay for animation
        setTimeout(() => {
          closeAccordions('.level2', 'active');
          closeAccordions('.level3', 'active');
        }, 300);
      };

      document
        .querySelectorAll('.level1-btn')
        .forEach((el) => el.addEventListener('click', (e) => toggleAccordion(e, 2)));
      document
        .querySelectorAll('.level2-btn')
        .forEach((el) => el.addEventListener('click', (e) => toggleAccordion(e, 3)));
      document
        .querySelectorAll('.level2_heading')
        .forEach((el) => el.addEventListener('click', moveLevel1AndCloseAccordions));
    });
  }

  initMainMenu() {
    const headerElements = document.querySelectorAll('[data-section="header"]');
    const parentLinks = document.querySelectorAll('.header__menu-parent');
    const desktopMenus = document.querySelectorAll('.header__menu-dropdown');
    const level2Items = document.querySelectorAll('.header__menu-level2-items');
    let currentTargetUl = null;
    let firstHover = true;

    // Toggle desktop menu on hover
    parentLinks.forEach((parentLink, index) => {
      parentLink.addEventListener('mouseover', function () {
        // Hide all other menus
        desktopMenus.forEach((menu, menuIndex) => {
          if (menuIndex !== index) {
            menu.classList.remove('open');
            menu.style.maxHeight = null;
            const currentNonActiveMenu = menu;
            const nonActiveLevel2Items = currentNonActiveMenu.querySelectorAll('.header__menu-level2-items');
            nonActiveLevel2Items.forEach((i) => {
              i.classList.remove('active');
              const id = i.dataset?.childMenu;
              const menuSecond = document.querySelector(`[data-menu-${id}]`);
              if (menuSecond) {
                menuSecond.classList.add('hide');
                const menuFirst = menuSecond?.parentElement?.querySelector('[data-menu-first]');
                if (menuFirst) {
                  menuFirst.classList.remove('hide');
                }
              }
            });
          }
        });

        // Handle the transition for the current menu
        if (!firstHover) {
          desktopMenus[index].style.transition = 'none';
        } else {
          desktopMenus[index].style.transition = 'max-height 0.5s ease-in-out';
        }

        // Show the current menu
        const scrollHeight = desktopMenus[index].scrollHeight + 48;
        desktopMenus[index].classList.add('open');
        desktopMenus[index].style.maxHeight = `${scrollHeight}px`;

        firstHover = false;
      });
    });

    level2Items.forEach((item) => {
      item.addEventListener('mouseover', function () {
        level2Items.forEach((i) => {
          i.classList.remove('active');
          const id = i.dataset?.childMenu;
          const menuSecond = document.querySelector(`[data-menu-${id}]`);
          if (menuSecond) {
            menuSecond.classList.add('hide');
            const menuFirst = menuSecond?.parentElement?.querySelector('[data-menu-first]');
            if (menuFirst) {
              menuFirst.classList.remove('hide');
            }
          }
        });
        this.classList.add('active');

        const targetId = this.dataset?.childMenu;
        const targetMenuSecond = document.querySelector(`[data-menu-${targetId}]`);
        if (targetMenuSecond) {
          targetMenuSecond.classList.remove('hide');
          const targetMenuFirst = targetMenuSecond?.parentElement?.querySelector('[data-menu-first]');
          if (targetMenuFirst) {
            targetMenuFirst.classList.add('hide');
          }
        }

        const targetUl = document.getElementById(targetId);

        if (currentTargetUl && currentTargetUl !== targetUl) {
          currentTargetUl.style.display = 'none';
        }

        if (targetUl) {
          targetUl.style.display = 'flex';
          currentTargetUl = targetUl;
        }
      });
    });

    // Handle mouseout events on headerElements to hide the menus
    headerElements.forEach((element) => {
      element.addEventListener('mouseleave', function () {
        desktopMenus.forEach((menu) => {
          menu.classList.remove('open');
          menu.style.maxHeight = null;
        });
        firstHover = true; // Reset the firstHover flag
      });
    });
  }

  initSearchSuggestions() {
    const rootUrl = window.theme.locale && !window.theme.locale.primary ? window.theme.locale.rootUrl : '';
    const maxRecentSearchesCount = window.theme.search.header.displayRecentSearches ? 4 : 0;
    const maxPopularSearchesCount = 4;

    window.Tagalys?.UIWidgets.SearchSuggestions.init(selectors.searchInput, {
      searchResultsURL: `${rootUrl}/search`,
      templates: {
        widget: {
          options: {
            includeTagalysCSS: false,
            request: {
              products: {
                limit: 6,
              },
            },
            maxRecentSearchesCount,
            maxTotalSearchesCount: maxRecentSearchesCount + maxPopularSearchesCount,
            inheritAlignmentElementWidth: false,
            position(inputDOMNode, widgetDOMNode) {
              const searchForm = inputDOMNode.parentElement;
              const widgetParent = widgetDOMNode.parentElement;
              searchForm.after(widgetParent);
              return false;
            },
          },
          callbacks: {
            afterInitialRender: (helpers) => {
              // Close widget on input blur and reset input value
              document.addEventListener('tagalys:hide-search-suggestions', () => {
                helpers.hideSearchSuggestionsPopover();
              });
            },
          },
        },
        hint: {
          render(html) {
            return html``;
          },
        },
        clickSuggestions: {
          render(html, args) {
            const props = args.props;
            const helpers = props.helpers;
            const recentAndPopularSearches = helpers.getRecentAndPopularSearches();

            const displayPopularSearches = window.theme.search.header.displayPopularSearches;
            const displaySuggestedSearches = window.theme.search.header.displaySuggestedSearches;
            const displayRecentSearches =
              window.theme.search.header.displayRecentSearches && recentAndPopularSearches.recentSearches?.length > 0;

            if (!displayPopularSearches && !displayRecentSearches && !displaySuggestedSearches) return '';

            return html`
              <div>
                ${displaySuggestedSearches
                  ? html`
                      <div class="search-suggestions__links text--regular">
                        <span>${window.theme.strings.search.header.suggested}</span>
                        ${recentAndPopularSearches.popularSearches
                          .slice(0, 3)
                          .map((term) => html` <${args.templates.suggestedTerm} suggestedTerm=${term} /> `)}
                      </div>
                    `
                  : null}
                ${displayRecentSearches
                  ? html`
                      <div class="search-suggestions__links text--regular">
                        <span>${window.theme.strings.search.header.recent}</span>
                        ${recentAndPopularSearches.recentSearches.map(
                          (term) => html` <${args.templates.suggestedTerm} suggestedTerm=${term} /> `
                        )}
                      </div>
                    `
                  : null}
                ${displayPopularSearches
                  ? html`
                      <div class="search-suggestions__links text--regular">
                        <span>${window.theme.strings.search.header.popular}</span>
                        ${recentAndPopularSearches.popularSearches
                          .slice(3, 6)
                          .map((term) => html` <${args.templates.suggestedTerm} suggestedTerm=${term} /> `)}
                      </div>
                    `
                  : null}
              </div>
            `;
          },
        },
        suggestedTerm: {
          render(html, args) {
            const props = args.props;
            const helpers = props.helpers;
            return html`
              <a href=${helpers.getLink(props.suggestedTerm.queryString)}>${props.suggestedTerm.displayString}</a>
            `;
          },
        },
        typeSuggestions: {
          render(html, args) {
            const props = args.props;
            const helpers = props.helpers;
            const searchTerm = props.helpers.getEnteredSearchQuery();

            const searchPageUrl = `${rootUrl}/search?q=${searchTerm}`;

            return html`
              <${args.templates.products} />

              <div class="search-suggestions__footer">
                <a href=${encodeURI(searchPageUrl)} class="btn--text btn--text-underline"
                  >${window.theme?.strings?.search?.viewAll}</a
                >
              </div>
            `;
          },
        },
        products: {
          render(html, args) {
            const props = args.props;
            const products = props.helpers.getProducts();
            const searchTerm = props.helpers.getEnteredSearchQuery();

            return html`
              <div class="search-suggestions__products">
                ${products.map(
                  (product, index) => html` <${args.templates.product} key=${index} product=${product} /> `
                )}
              </div>
            `;
          },
        },
        product: {
          render(html, args) {
            const props = args.props;
            const product = props.product;
            const helpers = props.helpers;

            const imageUrl = product.__tagalys_fields.image_url;
            const url = product.__tagalys_fields.link;

            return html`
              <div class="search-suggestions__product">
                <a href=${product.__tagalys_fields.link}>
                  <img
                    class="search-suggestions__product-image"
                    src=${product.__tagalys_fields.image_url}
                    alt=${product.title}
                  />
                  <span class="search-suggestions__product-title text--regular text--small-max-md"
                    >${product.title}</span
                  >
                  <div class="search-suggestions__product-price text--regular text--small-max-md">
                    ${helpers.isProductOnSale(product.compare_at_price, product.price)
                      ? html`
                          <span class="visually-hidden">${window.theme?.strings?.product?.regularPrice}</span>
                          <s data-compare-price>${helpers.formatCurrency(product.compare_at_price)}</s>
                        `
                      : null}
                    <span
                      class="${helpers.isProductOnSale(product.compare_at_price, product.price)
                        ? 'product-card__sale-price'
                        : null}"
                    >
                      ${helpers.formatCurrency(product.price)}
                    </span>
                  </div>
                </a>
              </div>
            `;
          },
        },
      },
      callbacks: {
        afterInitialRender: () => {
          this.searchWidget = document.querySelector('.tagalys-search-suggestions');
        },
        onSuccessfulAPICall: async (response) => {
          const staticTranslations = {};
          const translatedResponse = await TagalysMarketTranslations.translate(response, staticTranslations);
          return translatedResponse;
        },
      },
    });
  }

  initWishlistCount() {
    const wishlistCountElem = this.wishlistCountElem;
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(function (swat) {
      swat.renderWishlistCount(wishlistCountElem);
    });
  }
}

export default (function () {
  const headerEl = document.querySelector(selectors.section);
  if (!headerEl) return;

  const headerSection = new Header(headerEl);
  headerSection.init();
})();
